import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { find } from "lodash";
import { FormatDate } from "utils/helpers/date_helpers";
import { getGeomTypeLabel } from "utils/helpers/geo_helpers";

// Create styles
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    width: "100%",
    height: "100%",
    minHeight: 400,
  },
  row: {
    flexDirection: "row",
    width: "100%",
    fontSize: 12,
    padding: 10,
  },
  rowGray: {
    flexDirection: "row",
    width: "100%",
    fontSize: 12,
    padding: 10,
    backgroundColor: "#fafafa",
  },
  rowHeader: {
    flexDirection: "row",
    width: "100%",
    marginTop: 10,
    marginBottom: 10,
    fontSize: 14,
    fontWeight: "bold",
    padding: 10,
  },
  col: {
    flexDirection: "column",
    width: "50%",
    fontWeight: "normal",
  },
  colSmall: {
    flexDirection: "column",
    width: "50%",
    fontWeight: "normal",
    fontSize: 10,
  },
  title: {
    fontSize: 14,
    color: "#52c41a",
    fontWeight: "bold",
  },
  titleGray: {
    fontSize: 14,
    color: "#595959",
    fontWeight: "bold",
  },
});

const generateViewCharts = ({
  prjElement,
  simulationResponse,
  elementCharts,
  arrayChartTypes,
  treeTypesLegend,
}) => {
  return (
    <View key={prjElement?.uuid} break>
      <View style={styles.rowHeader}>
        <Text style={styles.title}>
          Simulazione elemento {getGeomTypeLabel(prjElement?.geom_type)}
          (Specie{" "}
          {
            find(treeTypesLegend, {
              id:
                prjElement?.geom_type === 3
                  ? simulationResponse?.type
                  : prjElement?.details?.type,
            })?.label
          }
          )
        </Text>
      </View>

      {elementCharts?.map((elementChart) => {
        const chartData = find(arrayChartTypes, {
          substance: elementChart?.type,
        });
        if (chartData) {
          return (
            <View
              key={`${prjElement?.uuid}_${elementChart?.type}`}
              style={{ marginBottom: 15, marginTop: 15 }}
            >
              <View style={styles.row}>
                <Text style={styles?.titleGray}>
                  <b>{chartData?.main_label}</b>
                </Text>
              </View>

              <View style={[styles.row, { paddingBottom: 4, paddingTop: 4 }]}>
                <Text style={styles?.colSmall}>
                  <b>Totale per m2</b>
                </Text>
                <Text style={styles?.colSmall}>
                  {simulationResponse?.[chartData?.data_tot]?.toFixed(4)}{" "}
                  {chartData?.unit}
                </Text>
              </View>

              <View
                style={[styles.rowGray, { paddingBottom: 4, paddingTop: 4 }]}
              >
                <Text style={styles?.colSmall}>
                  <b>Totale per albero</b>
                </Text>
                <Text style={styles?.colSmall}>
                  {simulationResponse?.[chartData?.data_tree]?.toFixed(4)}{" "}
                  {chartData?.unit}
                </Text>
              </View>

              <Image
                src={elementChart?.el?.getEchartsInstance()?.getDataURL()}
              />
            </View>
          );
        }
      })}
    </View>
  );
};

// Create Document Component
const PdfStatisticsAirTreeView = (props) => {
  return (
    <>
      <Document>
        <Page size="A4" style={styles.page}>
          <View>
            <Image src={props?.srcMapImage} />
          </View>
          <View style={styles.rowHeader}>
            <Text style={styles.title}>
              Nome progetto: {props?.project?.name}
            </Text>
          </View>
          <View style={styles.rowGray}>
            <Text style={styles?.col}>
              <b>Data inizio simulazione</b>
            </Text>
            <Text style={styles?.col}>
              {FormatDate(props?.project?.simulation_date_start)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles?.col}>
              <b>Data fine simulazione</b>
            </Text>
            <Text style={styles?.col}>
              {FormatDate(props?.project?.simulation_date_end)}
            </Text>
          </View>
          <View style={styles.rowGray}>
            <Text style={styles?.col}>
              <b>Latitudine</b>
            </Text>
            <Text style={styles?.col}>
              {props?.project?.centroid_lat?.toFixed(6)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles?.col}>
              <b>Longitudine</b>
            </Text>
            <Text style={styles?.col}>
              {props?.project?.centroid_lng?.toFixed(6)}
            </Text>
          </View>
          <View style={styles.rowGray}>
            <Text style={styles?.col}>
              <b>Superfice Km2</b>
            </Text>
            <Text style={styles?.col}>{props?.project?.area}</Text>
          </View>
          <View break>
            <View style={styles.rowHeader}>
              <Text style={styles.title}>
                Superficie totale copertura per specie
              </Text>
            </View>
            {props?.treeTypesLegend &&
              Object.keys(props?.project?.totalByTreeType).map((key) => {
                if (key != "totalPollutings") {
                  return (
                    <View
                      key={`${key}`}
                      style={{ marginBottom: 15, marginTop: 15 }}
                    >
                      <View
                        style={[
                          styles.row,
                          { paddingBottom: 4, paddingTop: 4 },
                        ]}
                      >
                        <Text style={styles?.colSmall}>
                          <b>
                            {
                              find(props?.treeTypesLegend, {
                                id: parseInt(key),
                              })?.label
                            }
                          </b>
                        </Text>
                        <Text style={styles?.colSmall}>
                          {(
                            props?.project?.totalByTreeType?.[key]?.area /
                            1000000
                          )?.toFixed(4)}{" "}
                          Km2
                        </Text>
                      </View>
                    </View>
                  );
                }
              })}
          </View>
          <View break>
            <View style={styles.rowHeader}>
              <Text style={styles.title}>Assorbimento totale inquinanti</Text>
            </View>
            {Object.keys(props?.project?.totalByTreeType?.totalPollutings).map(
              (key) => {
                let objTotPoll =
                  props?.project?.totalByTreeType?.totalPollutings?.[key];
                return (
                  <View
                    key={`${key}`}
                    style={{ marginBottom: 15, marginTop: 15 }}
                  >
                    <View
                      style={[styles.row, { paddingBottom: 4, paddingTop: 4 }]}
                    >
                      <Text style={styles?.colSmall}>
                        <b>
                          {
                            find(props?.arrayChartTypes, { substance: key })
                              ?.main_label
                          }
                        </b>
                      </Text>
                      <Text style={styles?.colSmall}>
                        {objTotPoll?.toFixed(2)}{" "}
                        {find(props?.arrayChartTypes, { substance: key })?.unit}
                      </Text>
                    </View>
                  </View>
                );
              }
            )}
          </View>
          {props?.project?.airtreeProjectElements?.map((prjElement) => {
            const elementCharts = props?.refChart?.current?.filter(
              (el) => el?.uuid === prjElement?.uuid
            );
            return prjElement?.geom_type === 3
              ? prjElement?.simulation_response?.map((simProjElement) => {
                  return generateViewCharts({
                    prjElement,
                    simulationResponse: simProjElement,
                    elementCharts,
                    arrayChartTypes: props?.arrayChartTypes,
                    treeTypesLegend: props?.treeTypesLegend,
                  });
                })
              : generateViewCharts({
                  prjElement,
                  simulationResponse: prjElement?.simulation_response,
                  elementCharts,
                  arrayChartTypes: props?.arrayChartTypes,
                  treeTypesLegend: props?.treeTypesLegend,
                });

            // return (
            //     <View key={prjElement?.uuid} break>
            //         <View style={styles.rowHeader}>
            //             <Text style={styles.title}>Simulazione elemento {getGeomTypeLabel(prjElement?.geom_type)} (Specie {find(props?.treeTypesLegend, { id: prjElement?.details?.type })?.label})</Text>
            //         </View>

            //         {elementCharts?.map(elementChart => {
            //             const chartData = find(props?.arrayChartTypes, { substance: elementChart?.type });
            //             if (chartData) {
            //                 return (
            //                     <View key={`${prjElement?.uuid}_${elementChart?.type}`} style={{ marginBottom: 15, marginTop: 15 }}>

            //                         <View style={styles.row}>
            //                             <Text style={styles?.titleGray}><b>{chartData?.main_label}</b></Text>
            //                         </View>

            //                         <View style={[styles.row, { paddingBottom: 4, paddingTop: 4 }]}>
            //                             <Text style={styles?.colSmall}><b>Totale per m2</b></Text>
            //                             <Text style={styles?.colSmall}>{prjElement?.simulation_response?.[chartData?.data_tot]?.toFixed(4)} {chartData?.unit}</Text>
            //                         </View>

            //                         <View style={[styles.rowGray, { paddingBottom: 4, paddingTop: 4 }]}>
            //                             <Text style={styles?.colSmall}><b>Totale per albero</b></Text>
            //                             <Text style={styles?.colSmall}>{prjElement?.simulation_response?.[chartData?.data_tree]?.toFixed(4)} {chartData?.unit}</Text>
            //                         </View>

            //                         <Image src={elementChart?.el?.getEchartsInstance()?.getDataURL()} />
            //                     </View>
            //                 )
            //             }
            //         })}
            //     </View>
            // )
          })}
        </Page>
      </Document>
    </>
  );
};

export default PdfStatisticsAirTreeView;
